import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Alert } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius, fonts } from '../theme';
import { type CustomerIllustration } from '../data/customers';
import { getSavedIllustrations, deleteSavedIllustrationLocal, SavedIllustration } from '../data/savedIllustrations';
import { CustomerQuickViewModal } from '../components/CustomerQuickViewModal';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { EditModal } from '../components/EditModal';
import { DateTimeViewer } from '../components/DateTimeViewer';
import { Toast, useToast } from '../components/Toast';
import { formatDate } from '../core/datetime';
import { illustrationRepository } from '../repositories/IllustrationRepository';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M11HistoryScreen'>;

interface HistoryIllustration extends CustomerIllustration {
  customerId: string; // MÃ KH
  isLocal?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const formatCurrencyDisplay = (val: string | number | undefined | null): string => {
  if (!val) return '0 đ';
  const str = String(val).trim();
  const digitsOnly = str.replace(/\D/g, '');
  if (!digitsOnly) return str;
  let num = parseInt(digitsOnly, 10);
  if (isNaN(num)) return str;
  if (num < 100000 && str.includes('.')) {
    num = num * 1000;
  }
  return `${num.toLocaleString('vi-VN')} đ`;
};

export const M11HistoryScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<'all' | 'new' | 'negotiating' | 'closed'>('all');
  const [historyList, setHistoryList] = useState<HistoryIllustration[]>([]);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  // Pagination states (AppPagination standard)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Confirm delete modal & toast states
  const [deletingItem, setDeletingItem] = useState<HistoryIllustration | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toastProps, showToast, hideToast } = useToast();

  // Quick view customer modal state
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);

  const handleOpenCustomerModal = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setIsCustomerModalVisible(true);
  };

  const handleCloseCustomerModal = () => {
    setIsCustomerModalVisible(false);
    setSelectedCustomerId(null);
  };

  // Edit proposal name states
  const [editingProposalItem, setEditingProposalItem] = useState<HistoryIllustration | null>(null);
  const [newProposalName, setNewProposalName] = useState<string>('');
  const [isEditProposalModalVisible, setIsEditProposalModalVisible] = useState<boolean>(false);

  const handleOpenEditProposal = (item: HistoryIllustration) => {
    setEditingProposalItem(item);
    setNewProposalName(item.proposalName);
    setIsEditProposalModalVisible(true);
  };

  const handleSaveProposalName = async () => {
    if (!editingProposalItem || !newProposalName.trim()) return;
    const trimmed = newProposalName.trim();
    try {
      await illustrationRepository.update(editingProposalItem.id, { proposalName: trimmed });
      showToast(`Đã đổi tên đề xuất thành "${trimmed}"`, 'success');
      setIsEditProposalModalVisible(false);
      setEditingProposalItem(null);
      await loadAllHistory();
    } catch (err) {
      showToast('Không thể cập nhật tên đề xuất', 'error');
    }
  };
  // Edit stage status states
  const [editingStageItem, setEditingStageItem] = useState<HistoryIllustration | null>(null);
  const [newStageValue, setNewStageValue] = useState<'new' | 'negotiating' | 'closed'>('new');
  const [isEditStageModalVisible, setIsEditStageModalVisible] = useState<boolean>(false);

  const handleOpenEditStage = (item: HistoryIllustration) => {
    setEditingStageItem(item);
    setNewStageValue(item.stage || 'new');
    setIsEditStageModalVisible(true);
  };

  const handleSaveStage = async () => {
    if (!editingStageItem) return;
    try {
      await illustrationRepository.update(editingStageItem.id, { stage: newStageValue });
      const label = STAGE_META[newStageValue]?.label || newStageValue;
      showToast(`Đã chuyển trạng thái thành "${label}"`, 'success');
      setIsEditStageModalVisible(false);
      setEditingStageItem(null);
      await loadAllHistory();
    } catch (err) {
      showToast('Không thể cập nhật trạng thái', 'error');
    }
  };

  const loadAllHistory = useCallback(async () => {
    const localItems = await illustrationRepository.getAll();
    const mappedLocal: HistoryIllustration[] = localItems.map((item) => ({
      id: item.id,
      proposalName: item.proposalName,
      customerName: item.customerName,
      customerAge: item.customerAge,
      customerGender: item.customerGender,
      sumAssured: item.sumAssured,
      premium: item.premium,
      freq: item.freq,
      term: String(item.term),
      selectedRiders: item.selectedRiders,
      topUpAmount: item.topUpAmount || 0,
      topUpStartYear: 1,
      topUpDuration: 0,
      r5Mode: 'complimentary',
      lastUpdated: item.updatedAt || item.lastUpdated,
      createdAt: item.createdAt || item.lastUpdated,
      updatedAt: item.updatedAt || item.lastUpdated,
      expiryDate: item.expiryDate || 'Tự động gia hạn',
      stage: item.stage,
      customerId: item.customerId || item.customerName,
      isLocal: true,
    }));
    
    // Filter out deleted items
    const filtered = mappedLocal.filter((item) => !deletedIds.has(item.id));

    setHistoryList(filtered);
  }, [deletedIds]);


  useFocusEffect(
    useCallback(() => {
      loadAllHistory();
    }, [loadAllHistory])
  );

  const filteredHistory = historyList.filter((item) => {
    const matchesSearch = item.customerName.toLowerCase().includes(search.toLowerCase()) ||
                         item.proposalName.toLowerCase().includes(search.toLowerCase());
    const matchesStage = selectedStage === 'all' || item.stage === selectedStage;
    return matchesSearch && matchesStage;
  });

  // Sort by updatedAt descending (newest / latest updated first)
  const sortedHistory = React.useMemo(() => {
    return [...filteredHistory].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.lastUpdated || 0).getTime();
      const dateB = new Date(b.updatedAt || b.lastUpdated || 0).getTime();
      return dateB - dateA;
    });
  }, [filteredHistory]);

  // Reset page when search or stage filter changes
  React.useEffect(() => {
    setPage(1);
  }, [search, selectedStage]);

  // Pagination calculation
  const totalItems = sortedHistory.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const paginatedItems = React.useMemo(() => {
    const start = (page - 1) * limit;
    return sortedHistory.slice(start, start + limit);
  }, [sortedHistory, page, limit]);

  const handleCreateNew = () => {
    navigation.navigate('M04ProductListScreen');
  };

  const handleDownloadPDF = (name: string) => {
    Alert.alert('Tải xuống PDF', `Đã tải xuống thành công tài liệu minh họa của khách hàng: ${name}`);
  };

  /**
   * Navigate to M07 Result Screen to VIEW the saved illustration.
   */
  const handleView = (item: HistoryIllustration) => {
    const rawEstPremium = item.premium || '15.000';
    const estPremium = rawEstPremium.includes('.') && rawEstPremium.length >= 7
      ? rawEstPremium
      : rawEstPremium + '.000';

    navigation.navigate('M07ResultScreen', {
      illustrationId: item.id,
      customerName: item.customerName,
      customerAge: item.customerAge,
      customerGender: item.customerGender,
      sumAssured: item.sumAssured,
      term: item.term,
      freq: item.freq,
      estPremium,
      selectedRiders: item.selectedRiders,
      topUpAmount: item.topUpAmount,
      topUpStartYear: item.topUpStartYear,
      topUpDuration: item.topUpDuration,
      r5Mode: item.r5Mode,
    });
  };

  /**
   * Navigate to M06 Configure Product Screen to EDIT the saved illustration.
   */
  const handleEdit = (item: HistoryIllustration) => {
    navigation.navigate('M06ConfigureProductScreen', {
      illustrationId: item.id,
      customerName: item.customerName,
      customerAge: item.customerAge,
      customerGender: item.customerGender,
    });
  };

  /**
   * Prompt confirmation modal for deleting any illustration item
   */
  const handlePromptDelete = (item: HistoryIllustration) => {
    setDeletingItem(item);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    setIsDeleting(true);
    try {
      if (deletingItem.isLocal) {
        await illustrationRepository.delete(deletingItem.id);
      }
      setDeletedIds((prev) => {
        const next = new Set(prev);
        next.add(deletingItem.id);
        return next;
      });
      showToast(`Đã xóa tài liệu minh họa của "${deletingItem.customerName}"`, 'success');
      setIsDeleteModalVisible(false);
      setDeletingItem(null);
    } catch (err) {
      showToast('Không thể xóa tài liệu minh họa', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Click tên KH → mở M16 trang chi tiết khách hàng.
   */
  const handleOpenCustomer = (item: HistoryIllustration) => {
    navigation.navigate('M16CustomerDetailScreen', { customerId: item.customerId });
  };


  const STAGE_META = {
    new: { label: 'Mới tạo', color: '#2563EB', bg: '#eff6ff' },
    negotiating: { label: 'Đang tư vấn', color: '#D97706', bg: '#fffbeb' },
    closed: { label: 'Đã chốt', color: '#00A651', bg: '#f0fdf4' },
  };

  return (
    <View style={styles.container}>
      {/* Header Panel with Title, Search and Create Button */}
      <View style={styles.headerPanel}>
        <View style={styles.searchWrapper}>
          <Icon name="search" size={18} color={colors.onSurfaceVariant} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Tìm kiếm theo tên KH hoặc tên Đề xuất..."
            value={search}
            onChangeText={setSearch}
            placeholderTextColor={colors.onSurfaceVariant}
          />
          {search ? (
            <TouchableOpacity onPress={() => setSearch('')}>
              <Icon name="close" size={18} color={colors.onSurfaceVariant} />
            </TouchableOpacity>
          ) : null}
        </View>

        <TouchableOpacity 
          style={styles.createBtn}
          onPress={handleCreateNew}
          accessibilityLabel="Tạo minh họa mới"
        >
          <Text style={styles.createBtnText}>+ Tạo minh họa mới</Text>
        </TouchableOpacity>
      </View>

      {/* Filter Tabs & Banner */}
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Banner lưu ý */}
        <View style={styles.alertBanner}>
          <Icon name="info" size={16} color="#D97706" style={styles.alertIcon} />
          <Text style={styles.alertText}>
            Lưu ý: Bảng minh họa đã lưu tại đây được tự động đồng bộ. Bạn có thể nhấn icon mắt để xem nhanh, icon bút để chỉnh sửa cấu hình, hoặc icon PDF để tải file xuống.
          </Text>
        </View>

        {/* Stage Filter Buttons */}
        <View style={styles.filterBar}>
          {(['all', 'new', 'negotiating', 'closed'] as const).map((stage) => {
            const isActive = selectedStage === stage;
            const count = stage === 'all' 
              ? historyList.length 
              : historyList.filter(i => i.stage === stage).length;
            return (
              <TouchableOpacity
                key={stage}
                style={[styles.filterBtn, isActive && styles.filterBtnActive]}
                onPress={() => setSelectedStage(stage)}
              >
                <Text style={[styles.filterBtnText, isActive && styles.filterBtnTextActive]}>
                  {stage === 'all' ? 'Tất cả' : STAGE_META[stage]?.label} ({count})
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Table Instructions */}
        <Text style={styles.scrollHelperText}>
          Swipe sang phải để xem đầy đủ các cột thông tin
        </Text>

        {/* Data Grid / Table Wrapper */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          style={styles.tableScroll}
          contentContainerStyle={styles.tableScrollContent}
        >
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, styles.colCustomer]}>Tên khách hàng</Text>
              <Text style={[styles.headerCell, styles.colProposal]}>Tên Đề xuất</Text>
              <Text style={[styles.headerCell, styles.colPremium, { textAlign: 'right' }]}>Phí bảo hiểm</Text>
              <Text style={[styles.headerCell, styles.colDate]}>Thời gian (Tạo / Sửa)</Text>
              <Text style={[styles.headerCell, styles.colStage]}>Trạng thái</Text>
              <Text style={[styles.headerCell, styles.colAction, { textAlign: 'center' }]}>Hành động</Text>
            </View>

            {/* Table Rows */}
            {paginatedItems.map((item, index) => (
              <View 
                key={item.id} 
                style={[
                  styles.tableRow, 
                  index % 2 === 1 && { backgroundColor: '#f9fafb' }
                ]}
              >
                <TouchableOpacity
                  style={[styles.rowCell, styles.colCustomer, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 6 }]}
                  onPress={() => handleOpenCustomerModal(item.customerId)}
                  accessibilityLabel={`Xem thông tin KH ${item.customerName}`}
                >
                  <Text style={[styles.customerName, { textAlign: 'left' }]}>{item.customerName}</Text>
                  <View style={styles.infoBadgeBtn}>
                    <Icon name="info" size={14} color={colors.primary} />
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.rowCell, styles.colProposal, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 6 }]}
                  onPress={() => handleOpenEditProposal(item)}
                  accessibilityLabel={`Đổi tên đề xuất ${item.proposalName}`}
                >
                  <Text style={[styles.proposalName, { textAlign: 'left' }]}>{item.proposalName}</Text>
                  <Icon name="edit" size={14} color={colors.onSurfaceVariant} />
                </TouchableOpacity>

                <View style={[styles.rowCell, styles.colPremium, { justifyContent: 'flex-end' }]}>
                  <Text style={styles.premiumText}>{formatCurrencyDisplay(item.premium)}</Text>
                </View>

                <View style={[styles.rowCell, styles.colDate, { flexDirection: 'column', gap: 2, alignItems: 'flex-start', justifyContent: 'center' }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Text style={styles.dateMetaLabel}>Tạo:</Text>
                    <DateTimeViewer date={item.createdAt} initialMode="relative" style={styles.dateText} />
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Text style={styles.dateMetaLabel}>Sửa:</Text>
                    <DateTimeViewer date={item.updatedAt || item.lastUpdated} initialMode="relative" style={styles.dateText} />
                  </View>
                </View>

                <TouchableOpacity
                  style={[styles.rowCell, styles.colStage]}
                  onPress={() => handleOpenEditStage(item)}
                  accessibilityLabel={`Sửa trạng thái ${item.proposalName}`}
                >
                  <View style={[styles.stageBadge, { backgroundColor: STAGE_META[item.stage]?.bg || '#eff6ff', flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
                    <Text style={[styles.stageBadgeText, { color: STAGE_META[item.stage]?.color || '#2563EB' }]}>
                      {STAGE_META[item.stage]?.label || 'Mới tạo'}
                    </Text>
                    <Icon name="expand_more" size={14} color={STAGE_META[item.stage]?.color || '#2563EB'} />
                  </View>
                </TouchableOpacity>

                <View style={[styles.rowCell, styles.colAction, { flexDirection: 'row', gap: spacing[1.5], justifyContent: 'center', alignItems: 'center' }]}>
                  <TouchableOpacity
                    style={[styles.actionIconBtn, styles.viewBtn]}
                    onPress={() => handleView(item)}
                    accessibilityLabel={`Xem minh họa của ${item.customerName}`}
                  >
                    <Icon name="visibility" size={18} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionIconBtn, styles.editBtn]}
                    onPress={() => handleEdit(item)}
                    accessibilityLabel={`Chỉnh sửa minh họa của ${item.customerName}`}
                  >
                    <Icon name="edit" size={18} color="#D97706" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionIconBtn}
                    onPress={() => handleDownloadPDF(item.customerName)}
                    accessibilityLabel={`Tải PDF của ${item.customerName}`}
                  >
                    <Icon name="picture_as_pdf" size={18} color="#EF4444" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.actionIconBtn}
                    onPress={() => handlePromptDelete(item)}
                    accessibilityLabel={`Xóa minh họa của ${item.customerName}`}
                  >
                    <Icon name="delete" size={18} color="#dc2626" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            {filteredHistory.length === 0 && (
              <View style={styles.emptyContainer}>
                <Icon name="history" size={48} color={colors.onSurfaceVariant} />
                <Text style={styles.emptyText}>Chưa có lịch sử minh họa nào khớp</Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* AppPagination Footer Bar (Standard Paginator) */}
        {totalItems > 0 && (
          <View style={styles.paginationContainer}>
            <Text style={styles.paginationInfoText}>
              Hiển thị {totalItems > 0 ? (page - 1) * limit + 1 : 0} - {Math.min(page * limit, totalItems)} trong tổng số {totalItems} kết quả
            </Text>

            <View style={styles.paginationActions}>
              <View style={styles.pageSizeGroup}>
                <Text style={styles.pageSizeLabel}>Hiển thị:</Text>
                {[10, 20, 50].map((size) => (
                  <TouchableOpacity
                    key={size}
                    style={[styles.pageSizeBadge, limit === size && styles.pageSizeBadgeActive]}
                    onPress={() => { setLimit(size); setPage(1); }}
                  >
                    <Text style={[styles.pageSizeText, limit === size && styles.pageSizeTextActive]}>{size}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <View style={styles.pageGroup}>
                <TouchableOpacity
                  style={[styles.pageBtn, page <= 1 && styles.pageBtnDisabled]}
                  onPress={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <Icon name="chevron_left" size={18} color={page <= 1 ? '#94a3b8' : colors.primary} />
                  <Text style={[styles.pageBtnText, page <= 1 && styles.pageBtnTextDisabled]}>Trước</Text>
                </TouchableOpacity>

                <View style={styles.pageBadge}>
                  <Text style={styles.pageBadgeText}>{page} / {totalPages}</Text>
                </View>

                <TouchableOpacity
                  style={[styles.pageBtn, page >= totalPages && styles.pageBtnDisabled]}
                  onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <Text style={[styles.pageBtnText, page >= totalPages && styles.pageBtnTextDisabled]}>Sau</Text>
                  <Icon name="chevron_right" size={18} color={page >= totalPages ? '#94a3b8' : colors.primary} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </ScrollView>

      {/* CUSTOMER QUICK VIEW POPUP MODAL */}
      <CustomerQuickViewModal
        visible={isCustomerModalVisible}
        customerId={selectedCustomerId}
        onClose={handleCloseCustomerModal}
        onViewIllustration={(illust) => {
          handleView(illust as any);
        }}
        onEditIllustration={(illust) => {
          handleEdit(illust as any);
        }}
        onDownloadPDF={(illust) => {
          handleDownloadPDF(illust.customerName);
        }}
        onCreateNewIllustration={(name, age, gender) => {
          navigation.navigate('M06ConfigureProductScreen', {
            illustrationId: undefined,
          });
        }}
        onViewFullDetail={(cId) => {
          navigation.navigate('M16CustomerDetailScreen', { customerId: cId });
        }}
      />

      {/* EDIT PROPOSAL NAME MODAL */}
      <EditModal
        visible={isEditProposalModalVisible}
        title="Đổi tên đề xuất"
        subtitle={`Khách hàng: ${editingProposalItem?.customerName || ''}`}
        saveLabel="Lưu thay đổi"
        fields={[
          {
            index: 1,
            label: 'Tên Đề xuất minh họa',
            type: 'text',
            value: newProposalName,
            onChange: setNewProposalName,
            hint: 'Nhập tên đề xuất mới cho tài liệu này',
          },
        ]}
        onCancel={() => {
          setIsEditProposalModalVisible(false);
          setEditingProposalItem(null);
        }}
        onSave={handleSaveProposalName}
      />
      {/* EDIT STAGE STATUS MODAL */}
      <EditModal
        visible={isEditStageModalVisible}
        title="Cập nhật trạng thái đề xuất"
        subtitle={`Đề xuất: ${editingStageItem?.proposalName || ''}`}
        saveLabel="Cập nhật"
        fields={[
          {
            index: 1,
            label: 'Trạng thái xử lý',
            type: 'dropdown',
            value: newStageValue,
            onChange: (v) => setNewStageValue(v as any),
            options: [
              { label: 'Mới tạo', value: 'new' },
              { label: 'Đang tư vấn', value: 'negotiating' },
              { label: 'Đã chốt', value: 'closed' },
            ],
          },
        ]}
        onCancel={() => {
          setIsEditStageModalVisible(false);
          setEditingStageItem(null);
        }}
        onSave={handleSaveStage}
      />

      {/* CONFIRM DELETE MODAL */}
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        proposalName={deletingItem?.proposalName}
        customerName={deletingItem?.customerName}
        onClose={() => {
          setIsDeleteModalVisible(false);
          setDeletingItem(null);
        }}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />

      {/* TOAST NOTIFICATION */}
      <Toast {...toastProps} onHide={hideToast} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  headerPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing[4],
    backgroundColor: '#ffffff',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
    gap: spacing[2],
  },
  searchWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    backgroundColor: colors.surface,
    height: 40,
  },
  searchIcon: {
    marginRight: spacing[2],
  },
  searchInput: {
    flex: 1,
    height: '100%',
    color: colors.onSurface,
    fontSize: 13,
  },
  createBtn: {
    backgroundColor: '#EF4444',
    paddingHorizontal: spacing[4],
    height: 40,
    borderRadius: borderRadius.DEFAULT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  createBtnText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 13,
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 88,
  },
  alertBanner: {
    flexDirection: 'row',
    backgroundColor: '#FFFDF5',
    borderWidth: 1,
    borderColor: '#FEF3C7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    marginBottom: spacing[4],
    gap: spacing[2],
  },
  alertIcon: {
    marginTop: 2,
  },
  alertText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 16,
    color: '#B45309',
    textAlign: 'justify',
  },
  scrollHelperText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[2],
    fontStyle: 'italic',
  },
  tableScroll: {
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    backgroundColor: '#ffffff',
    width: '100%',
  },
  tableScrollContent: {
    minWidth: '100%',
  },
  table: {
    width: '100%',
    minWidth: 910,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[3],
    width: '100%',
  },
  headerCell: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    paddingHorizontal: spacing[3],
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    paddingVertical: spacing[4],
    width: '100%',
  },
  rowCell: {
    paddingHorizontal: spacing[3],
    justifyContent: 'center',
  },
  colCustomer: {
    minWidth: 140,
    flex: 1.5,
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  colProposal: {
    minWidth: 220,
    flex: 2.5,
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  colPremium: {
    minWidth: 130,
    flex: 1.3,
    alignItems: 'flex-end',
    justifyContent: 'center',
    textAlign: 'right',
  },
  colDate: {
    minWidth: 110,
    flex: 1,
  },
  colStage: {
    minWidth: 110,
    flex: 1,
  },
  colAction: {
    minWidth: 180,
    flex: 1.5,
  },
  customerName: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.primary,
  },
  proposalName: {
    fontSize: 13,
    color: colors.onSurface,
  },
  premiumText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    textAlign: 'right',
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
  dateText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  statusText: {
    fontSize: 12,
    color: '#374151',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[10],
    gap: spacing[3],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  footerNote: {
    marginTop: spacing[3],
  },
  footerNoteText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: '500',
  },

  /* STAGE FILTER BAR STYLES */
  filterBar: {
    flexDirection: 'row',
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[2],
    gap: spacing[2],
    backgroundColor: '#ffffff',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  filterBtn: {
    paddingVertical: spacing[1.5],
    paddingHorizontal: spacing[3],
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: colors.outline,
    backgroundColor: '#ffffff',
  },
  filterBtnActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  filterBtnText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  filterBtnTextActive: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },

  /* PIPELINE STAGE BADGES */
  stageBadge: {
    alignSelf: 'flex-start',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  stageBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },

  /* ROW ACTION BUTTONS */
  actionIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  viewBtn: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
  },
  editBtn: {
    backgroundColor: '#FFF7ED',
    borderColor: '#F59E0B',
  },
  infoBadgeBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* PAGINATION STYLES (AppPagination standard) */
  dateMetaLabel: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    color: '#64748b',
    minWidth: 26,
  },
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[4],
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
    flexWrap: 'wrap',
    gap: spacing[3],
    marginTop: spacing[2],
  },
  paginationInfoText: {
    fontSize: 12,
    color: '#64748b',
  },
  paginationActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
    flexWrap: 'wrap',
  },
  pageSizeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  pageSizeLabel: {
    fontSize: 12,
    color: '#64748b',
    marginRight: 4,
  },
  pageSizeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: borderRadius.sm,
    backgroundColor: '#f1f5f9',
  },
  pageSizeBadgeActive: {
    backgroundColor: colors.primary,
  },
  pageSizeText: {
    fontSize: 12,
    color: '#475569',
  },
  pageSizeTextActive: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },
  pageGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  pageBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing[2.5],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.outline,
    backgroundColor: '#ffffff',
    gap: 2,
  },
  pageBtnDisabled: {
    borderColor: '#e2e8f0',
    backgroundColor: '#f8fafc',
  },
  pageBtnText: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },
  pageBtnTextDisabled: {
    color: '#94a3b8',
  },
  pageBadge: {
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.sm,
    backgroundColor: '#f1f5f9',
  },
  pageBadgeText: {
    fontSize: 12,
    color: '#0f172a',
    fontWeight: fontWeights.bold,
  },
});

